import type { ActLine, InvoiceLine, LineInput, Money } from '@/api/types';

/** A line while it is typed: quantity and price stay text until the form is sent. */
export type LineDraft = { key: number; title: string; description: string; quantity: string; unit: string; price: string; source?: string };

let seq = 0;
export const blankLine = (patch: Partial<LineDraft> = {}): LineDraft => ({ key: ++seq, title: '', description: '', quantity: '1', unit: 'усл.', price: '', ...patch });

/** «1,5» or «1.5» → 1.5; null when it is not a number. */
export function parseNumber(text: string): number | null {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

export const parseQuantity = (text: string): number | null => {
  const n = parseNumber(text);
  // The BFF keeps 3 decimals, 0 < q <= 10000.
  return n !== null && n > 0 && n <= 10_000 && Math.abs(n * 1000 - Math.round(n * 1000)) < 1e-6 ? n : null;
};

/** Rubles typed by a person → minor units; null when it is not an amount with up to 2 decimals. */
export function parsePriceMinor(text: string): number | null {
  const n = parseNumber(text);
  if (n === null || Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) return null;
  return Math.round(n * 100);
}

export const minorToText = (minor: number) => (minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2).replace('.', ','));

/** What one line adds up to, in minor units, the way the BFF computes it. */
export function lineTotalMinor(l: LineDraft): number {
  const q = parseQuantity(l.quantity);
  const p = parsePriceMinor(l.price);
  return q === null || p === null ? 0 : Math.round(q * p);
}

export type LineProblem = 'title' | 'quantity' | 'price';

/** The first thing wrong with a line; `allowFreePrice`: an act line may wait for its price. */
export function lineProblem(l: LineDraft, allowFreePrice = false): LineProblem | null {
  if (!l.title.trim()) return 'title';
  if (parseQuantity(l.quantity) === null) return 'quantity';
  const p = parsePriceMinor(l.price);
  if (p === null || (!allowFreePrice && p <= 0)) return 'price';
  return null;
}

export const problemText: Record<LineProblem, string> = {
  title: 'Название строки',
  quantity: 'Количество — число больше нуля, до трёх знаков после запятой',
  price: 'Цена — сумма больше нуля',
};

export function toInput(l: LineDraft): LineInput & { source?: string } {
  return {
    title: l.title.trim(),
    ...(l.description.trim() ? { description: l.description.trim() } : {}),
    quantity: parseQuantity(l.quantity) ?? 1,
    unit: l.unit.trim() || 'усл.',
    price: parsePriceMinor(l.price) ?? 0,
    ...(l.source ? { source: l.source } : {}),
  };
}

export const fromInput = (l: LineInput & { source?: string }): LineDraft => blankLine({ title: l.title, description: l.description ?? '', quantity: String(l.quantity).replace('.', ','), unit: l.unit, price: l.price ? minorToText(l.price) : '', source: l.source });

export const fromInvoiceLine = (l: InvoiceLine): LineDraft =>
  blankLine({ title: l.title, description: l.note ?? '', quantity: String(l.quantity).replace('.', ','), unit: l.unit ?? 'усл.', price: minorToText(l.price.amountMinor) });

export const fromActLine = (l: ActLine): LineDraft =>
  blankLine({ title: l.title, description: l.description ?? '', quantity: String(l.quantity).replace('.', ','), unit: l.unit, price: l.price.amountMinor ? minorToText(l.price.amountMinor) : '', source: l.source ?? undefined });

export const sumOf = (lines: LineDraft[], currency: Money['currency']): Money => ({ amountMinor: lines.reduce((n, l) => n + lineTotalMinor(l), 0), currency });

/** An ISO date `days` from today, for a date field. */
export function isoInDays(days: number, from = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
